import { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { TEmployeeFormData, TFormType } from 'src/@types';
import { RegUpdText } from 'src/components/custom-regUpd-text/RegUpdText';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import RHFMultiAutocomplete from 'src/components/hook-form/RHFMultiAutocomplete';
import {
  FormInnerContainer,
  FormItem,
  FormLabel,
  FormRow,
  StyledLoadingButton,
} from 'src/layouts/form/FormLayout';
import { IEmployee, ILabelValueModel, useStores } from 'src/models';
import { employeeApi, groupApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';
import { getEmployeeFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import * as Yup from 'yup';

interface EmployeeFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedEmployee?: IEmployee;
}
const EmployeeFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedEmployee }: EmployeeFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { enqueueSnackbar } = useSnackbar();
    const { alertStore } = useStores();
    const isCreate = formType === FormType.CREATE;
    // 중복확인 상태
    const [isConfirmDuplicate, setIsConfirmDuplicate] = useState(false);
    // 중복확인 버튼 로딩 상태
    const [checkDuplicateButtonLoading, setCheckDuplicateButtonLoading] = useState(false);

    const methods = useForm<TEmployeeFormData>({
      mode: 'all',
      context: { formType, isConfirmDuplicate },
      defaultValues: getEmployeeFormDefaultValues(selectedEmployee),
    });

    const { getValues, setValue, handleSubmit } = methods;

    // 그룹 정보 select 옵션 목록 조회
    const [OPRTR_GROUP_LIST, setOPRTR_GROUP_LIST] = useState<ILabelValueModel[]>([]);
    useEffect(() => {
      const fetchGroupList = async () => {
        const result = await groupApi.getGroups();
        if (result && result.success) {
          const groupList = result.data.map((group) => ({
            label: group.oprtrGroupNm,
            value: group.oprtrGroupSid,
          }));
          setOPRTR_GROUP_LIST(groupList);
        }
      };
      fetchGroupList();
    }, []);

    // Yup schema에서 이메일 형식 확인
    const loginIdValidationSchema = Yup.string().required('아이디를 입력하세요.');

    // 아이디 중복확인
    const handleCheckIdDuplicate = async () => {
      setCheckDuplicateButtonLoading(true);
      try {
        const loginId = getValues('loginId');
        try {
          loginIdValidationSchema.validateSync(loginId);
        } catch (validationError) {
          enqueueSnackbar(
            validationError instanceof Error ? validationError.message : 'Unknown error occurred',
            { variant: 'error' },
          );
          setCheckDuplicateButtonLoading(false);
          return;
        }
        // 중복 확인 DB 조회 + 확인
        if (loginId.trim()) {
          const result = await employeeApi.checkLoginIdDuplicate(loginId);
          if (result) {
            enqueueSnackbar('사용 가능한 아이디입니다.', { variant: 'success' });
            setIsConfirmDuplicate(true);
            methods.clearErrors('loginId');
            return;
          }
          // 중복된 아이디
          enqueueSnackbar('이미 사용중인 아이디입니다.', { variant: 'error' });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setCheckDuplicateButtonLoading(false);
      }
    };

    // 아이디 변경 시 중복확인 상태 초기화
    const handleLoginIdChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsConfirmDuplicate(false);
      setValue('loginId', e.target.value);
    };

    // 등록 / 수정 함수
    const onSubmit = async (data: TEmployeeFormData) => {
      const empInfo: TEmployeeFormData = {
        // ...data,
        ...(!isCreate && { oprtrSid: data.oprtrSid }),
        ...(data.pwd && { pwd: data.pwd }),
        ...(data.pwdCorrect && { pwdCorrect: data.pwdCorrect }),
        oprtrGroupSidList: data.oprtrGroupList?.map((item) => Number(item.value)),
        oprtrNm: data.oprtrNm,
        loginId: data.loginId,
      };
      const responseText = isCreate ? '등록' : '수정';
      const result = isCreate
        ? employeeApi.createEmployee(empInfo)
        : employeeApi.updateEmployee(empInfo);
      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = async () => {
      alertStore.setOnConfirm(async () => {
        if (!selectedEmployee?.oprtrSid) return;
        employeeApi
          .deleteEmployee(selectedEmployee?.oprtrSid)
          .then(() =>
            handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
          );
      });
      alertStore.setProps({
        open: true,
        title: '직원 삭제',
        content: '해당 직원을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    return (
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)} formId={FORM_IDS.EMPLOYEE}>
        <FormInnerContainer>
          <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                label={t('employee.form.oprtrNm')}
                name="oprtrNm"
                readonly={!isCreate}
              />
            </FormItem>
          </FormRow>
          {isCreate ? (
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFMultiAutocomplete
                  name="oprtrGroupList"
                  label={t('employee.form.oprtrGroupList')}
                  options={OPRTR_GROUP_LIST}
                  // getOptionLabel={(option) => option.oprtrGroupNm}
                />
              </FormItem>
            </FormRow>
          ) : (
            <FormRow sx={{ gridTemplateColumns: '1fr 1fr 2fr' }}>
              <FormItem>
                <RHFTextField label={t('employee.form.oprtrSid')} name="oprtrSid" readonly={true} />
              </FormItem>
              <FormItem>
                <RHFTextField
                  name="oprtrSttsCd.value"
                  label={t('employee.form.oprtrSttsCd')}
                  value={selectedEmployee?.oprtrSttsCd?.value || ''}
                  readonly={!isCreate}
                />
              </FormItem>
              <FormItem>
                <RHFMultiAutocomplete
                  name="oprtrGroupList"
                  label={t('employee.form.oprtrGroupList')}
                  options={OPRTR_GROUP_LIST}
                  selectedList={getValues('oprtrGroupList')}
                />
              </FormItem>
            </FormRow>
          )}
        </FormInnerContainer>

        <FormInnerContainer>
          <FormLabel>{t('common.form.section.accountInfo')}</FormLabel>
          <FormRow sx={{ gridTemplateColumns: formType === FormType.CREATE ? '4fr 0.5fr' : '1fr' }}>
            <FormItem>
              <RHFTextField
                name="loginId"
                label={t('employee.form.loginId')}
                placeholder={t('employee.form.loginIdPlaceholder')}
                onChange={handleLoginIdChange}
                readonly={!isCreate}
              />
            </FormItem>
            {isCreate && (
              <FormItem>
                <StyledLoadingButton
                  variant="outlined"
                  color="primary"
                  onClick={handleCheckIdDuplicate}
                  loading={checkDuplicateButtonLoading}
                  disabled={isConfirmDuplicate}
                >
                  {t('common.button.checkDuplicate')}
                </StyledLoadingButton>
              </FormItem>
            )}
          </FormRow>

          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField label={t('employee.form.pwd')} name="pwd" type="password" />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                label={t('employee.form.pwdCorrect')}
                name="pwdCorrect"
                type="password"
              />
            </FormItem>
          </FormRow>
        </FormInnerContainer>

        {!isCreate && (
          <RegUpdText
            updDt={selectedEmployee?.updDt ?? 0}
            userNm={selectedEmployee?.updUser?.oprtrNm ?? '-'}
          />
        )}

        <DialogActions>
          {!isCreate && (
            <Button onClick={handleDelete} variant="contained" color="error">
              {t('common.button.delete')}
            </Button>
          )}
          <Button type="submit" variant="contained" form={FORM_IDS.EMPLOYEE}>
            {isCreate ? t('common.button.register') : t('common.button.edit')}
          </Button>
        </DialogActions>
      </FormProvider>
    );
  },
);

export default EmployeeFormDialog;
